import { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

export function LegalLayout({ content, title }) {
  const [activeSection, setActiveSection] = useState('');
  const [sections, setSections] = useState([]);

  // Parse headings from content
  useEffect(() => {
    if (content) {
      const headingRegex = /^##\s+(.+)$/gm;
      const matches = [];
      let match;
      while ((match = headingRegex.exec(content)) !== null) {
        const id = match[1].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        matches.push({
          id,
          title: match[1].replace(/^\d+\.\s*/, ''),
        });
      }
      setSections(matches);
      if (matches.length > 0 && !activeSection) {
        setActiveSection(matches[0].id);
      }
    }
  }, [content, activeSection]);

  // Handle scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('.legal-content h2');
      let current = '';
      
      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          current = heading.id;
        }
      });
      
      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  // Convert markdown to HTML with proper IDs
  const renderContent = (markdown) => {
    if (!markdown) return '';
    
    let html = markdown
      // H1
      .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
      // H2 with ID
      .replace(/^##\s+(.+)$/gm, (match, p1) => {
        const id = p1.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return `<h2 id="${id}">${p1}</h2>`;
      })
      // H3
      .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Unordered list items
      .replace(/^-\s+(.+)$/gm, '<li>$1</li>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr />')
      // Paragraphs (lines that don't start with < or are empty)
      .split('\n')
      .map(line => {
        if (line.trim() === '') return '';
        if (line.startsWith('<')) return line;
        return `<p>${line}</p>`;
      })
      .join('\n');

    // Wrap consecutive li elements in ul
    html = html.replace(/(<li>.*?<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`);

    return html;
  };

  return (
    <div className="flex gap-8" data-testid="legal-layout">
      {/* Sidebar - Table of Contents */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-24">
          <h3 className="text-sm font-semibold text-foreground mb-4 tracking-wide uppercase">
            Table of Contents
          </h3>
          <ScrollArea className="h-[calc(100vh-200px)]">
            <nav className="space-y-1" data-testid="toc-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  data-testid={`toc-item-${section.id}`}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all flex items-center gap-2 ${
                    activeSection === section.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`}
                >
                  <ChevronRight
                    className={`w-3 h-3 transition-transform ${
                      activeSection === section.id ? 'rotate-90' : ''
                    }`}
                  />
                  <span className="truncate">{section.title}</span>
                </button>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <div 
          className="legal-content max-w-3xl"
          dangerouslySetInnerHTML={{ __html: renderContent(content) }}
          data-testid="legal-content"
        />
      </main>
    </div>
  );
}
